// File: src/hooks/useImagePreloader.js
import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useImagePreloader(urls[], options)
 *
 * - urls: array of image URLs to preload
 * - options:
 *    concurrency (default 3)
 *
 * Returns:
 * {
 *   statusMap: { [url]: 'pending'|'loading'|'loaded'|'error' },
 *   prioritize: (urlsToPrioritize: string[]|string) => void,
 *   isAllLoaded: boolean
 * }
 *
 * Behavior:
 * - starts background loading of all urls with limited concurrency
 * - call prioritize(urls) to immediately enqueue those urls at high priority
 *   (if not already loaded) and start loading them ASAP (concurrently).
 */
export default function useImagePreloader(initialUrls = [], options = {}) {
  const concurrency = options.concurrency ?? 3;

  // status map for UI
  const [statusMap, setStatusMap] = useState(() => {
    const map = {};
    (initialUrls || []).forEach((u) => (map[u] = "pending"));
    return map;
  });

  const queueRef = useRef([]); // FIFO queue of urls to load
  const loadingSetRef = useRef(new Set()); // urls currently loading
  const stoppedRef = useRef(false);
  const allUrlsRef = useRef(new Set(initialUrls || []));

  // Initialize queue and status map when initialUrls change
  useEffect(() => {
    // add any new URLs
    const known = allUrlsRef.current;
    const toAdd = [];
    (initialUrls || []).forEach((u) => {
      if (!known.has(u)) {
        known.add(u);
        toAdd.push(u);
      }
    });

    if (toAdd.length) {
      // mark pending and push at end of queue
      setStatusMap((prev) => {
        const next = { ...prev };
        toAdd.forEach((u) => (next[u] = "pending"));
        return next;
      });
      queueRef.current.push(...toAdd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialUrls || [])]); // stringify to catch content changes

  // helper to safely set status (use functional update)
  const setStatus = useCallback((url, status) => {
    setStatusMap((prev) => {
      if (prev[url] === status) return prev;
      return { ...prev, [url]: status };
    });
  }, []);

  // load single image (returns a promise)
  const loadImage = useCallback(
    (url) => {
      return new Promise((resolve) => {
        // if already loaded or loading, resolve immediately
        const cur = statusMap[url];
        if (cur === "loaded") return resolve({ url, status: "loaded" });
        if (loadingSetRef.current.has(url))
          return resolve({ url, status: "loading" });

        loadingSetRef.current.add(url);
        setStatus(url, "loading");

        const img = new Image();
        img.onload = () => {
          loadingSetRef.current.delete(url);
          setStatus(url, "loaded");
          resolve({ url, status: "loaded" });
        };
        img.onerror = () => {
          loadingSetRef.current.delete(url);
          setStatus(url, "error");
          resolve({ url, status: "error" });
        };
        // start loading
        img.src = url;
      });
    },
    [setStatus, statusMap]
  );

  // worker that consumes the queue with the given concurrency
  useEffect(() => {
    stoppedRef.current = false;
    let cancelled = false;

    async function workerLoop() {
      const inflight = new Set();

      async function startNext() {
        if (cancelled) return;
        // while we have capacity and queue is not empty, start next
        while (
          !cancelled &&
          loadingSetRef.current.size + inflight.size < concurrency &&
          queueRef.current.length > 0
        ) {
          const url = queueRef.current.shift();
          // skip if already loaded
          const st = statusMap[url];
          if (st === "loaded") continue;
          // mark as loading in inflight set
          inflight.add(url);
          // start load (don't await; handle completion)
          loadImage(url).then(() => {
            inflight.delete(url);
            // try to start another
            startNext();
          });
        }
      }

      // kick off initial batch
      startNext();

      // If queue becomes empty and no inflight, just resolve (no persistent loop)
      // But also periodically check until both inflight and queue empty (to handle items added later)
      while (!cancelled) {
        if (
          queueRef.current.length === 0 &&
          loadingSetRef.current.size === 0 &&
          inflight.size === 0
        ) {
          break;
        }
        // wait small time then check
        await new Promise((r) => setTimeout(r, 250));
        // attempt to start more if possible
        startNext();
      }
    }

    workerLoop();

    return () => {
      cancelled = true;
      stoppedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadImage, concurrency, JSON.stringify(Object.keys(statusMap || {}))]);

  // prioritize: bring urls to front of queue and start loading them immediately
  const prioritize = useCallback(
    (urls) => {
      if (!urls) return;
      const arr = Array.isArray(urls) ? urls : [urls];
      // ensure in allUrls set and set status pending if unknown
      arr.forEach((u) => {
        if (!u) return;
        if (!allUrlsRef.current.has(u)) {
          allUrlsRef.current.add(u);
          setStatus(u, "pending");
        }
        // remove all existing occurrences from queue
        queueRef.current = queueRef.current.filter((x) => x !== u);
      });

      // prepend prioritized urls
      queueRef.current = [...arr.filter(Boolean), ...queueRef.current];

      // immediately start loading prioritized urls (concurrently up to concurrency)
      // if they're not already loading/loaded
      arr.forEach((u) => {
        if (!u) return;
        const st = statusMap[u];
        if (st === "loaded" || loadingSetRef.current.has(u)) return;
        // Start the load directly (don't block; loadImage handles duplicate protection)
        loadImage(u);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [loadImage, statusMap]
  );

  // derived flag
  const isAllLoaded = useCallback(() => {
    const keys = Object.keys(statusMap || {});
    if (keys.length === 0) return false;
    return keys.every((k) => statusMap[k] === "loaded");
  }, [statusMap]);

  return {
    statusMap,
    prioritize,
    isAllLoaded: isAllLoaded(),
  };
}
